import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Unstable_Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Stack, TextField, Divider, Button, Fab } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddIcon from "@mui/icons-material/Add";
import {
  NormalizedCargo,
  NormalizedCargoItem,
} from "../../../../domain/models/cargo-model";
import { LoadNormalizedCargos } from "../../../../domain/usecases/load-normalized-cargos";
import ItemsDialog from "../components/items-dialog";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";
import { DeleteCargo } from "../../../../domain/usecases/delete-cargo";
import AlertDialog from "../../components/AlertDialog";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../../hooks";
import { PrintCargoLabel } from "../../../../domain/usecases/print-label/print-cargo-label";
import CargoCard from "./components/cargo-card";
import OverwriteCargoDialog from "../components/overwrite-cargo-dialog/overwrite-cargo-dialog";
import { LoadCargo } from "../../../../domain/usecases/load-cargo";
import { OverwriteCargo } from "../../../../domain/usecases/overwrite-cargo";

type Props = {
  loadCargos: LoadNormalizedCargos;
  loadCargo: LoadCargo;
  loadProducts: LoadProductListByManyCodes;
  deleteCargo: DeleteCargo;
  overwriteCargo: OverwriteCargo;
  printCargoLabel: PrintCargoLabel;
};

export const SeparationMain: React.FC<Props> = ({
  loadCargos,
  loadProducts,
  deleteCargo,
  printCargoLabel,
  loadCargo,
  overwriteCargo,
}) => {
  const [cargos, setCargos] = useState<NormalizedCargo[]>([]);
  const [delayedCargos, setDelayedCargos] = useState<NormalizedCargo[]>([]);
  const [filteredCargos, setFilteredCargos] = useState<NormalizedCargo[]>([]);
  const [items, setItems] = useState<NormalizedCargoItem[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [date, setDate] = React.useState<Dayjs>(dayjs(Date.now()));
  const [deleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const [overwriteOpen, setOverwriteOpen] = React.useState<boolean>(false);
  const [cargoId, setCargoId] = React.useState<number | null>(null);
  const [reload, setReload] = React.useState<boolean>(false);
  const navigate = useNavigate();

  const handleError = useErrorHandler((error) => {
    setCargos([]);
    setFilteredCargos([]);
    alert("Não foi possível carregar as cargas");
  });

  useEffect(() => {
    async function fetch() {
      try {
        const warehouseString = localStorage.getItem("currentWarehouse");
        const warehouse = warehouseString
          ? (JSON.parse(warehouseString) as {
              id: number;
              name: string;
            } | null)
          : undefined;
        const cargos = await loadCargos.load(warehouse!.id, {
          date: date.toDate().toISOString().slice(0, 10),
        });
        setCargos(cargos);
        setFilteredCargos(cargos);
      } catch (error: any) {
        handleError(error);
      }
    }
    fetch();
  }, [date, reload]);

  useEffect(() => {
    async function fetch() {
      var initialDate = new Date();
      var endDate = new Date();

      // Subtract two days
      initialDate.setDate(initialDate.getDate() - 2);
      endDate.setDate(endDate.getDate() - 1);
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;
      try {
        const cargos = await loadCargos.load(warehouse!.id, {
          status: ["NOT_CHECKED", "PENDING"],
          dateRange: [
            initialDate.toISOString().slice(0, 10),
            endDate.toISOString().slice(0, 10),
          ],
        });
        setDelayedCargos(cargos);
      } catch (error: any) {
        handleError(error);
      }
    }
    fetch();
  }, [reload]);

  const handleDelete = async () => {
    try {
      const deleted = await deleteCargo.delete(cargoId!);
      setDeleteOpen(false);
      setReload(!reload);
      if (deleted) return alert("Carga deletada com sucesso!");
    } catch (error) {
      alert("Não foi possível deletar a carga");
      setCargoId(null);
      setDeleteOpen(false);
    }
  };

  return (
    <Stack sx={{ m: "40px" }} spacing={4}>
      <AlertDialog
        open={deleteOpen}
        handleClose={() => {
          setDeleteOpen(false);
        }}
        title="Tem certeza que deseja deletar essa carga?"
        onConfirm={handleDelete}
      />
      <OverwriteCargoDialog
        open={overwriteOpen}
        handleClose={() => {
          setOverwriteOpen(false);
        }}
        loadCargo={loadCargo}
        overwriteCargo={overwriteCargo}
        oldCargoId={cargoId!}
        onSuccess={() => {
          setReload(!reload);
        }}
      />
      <ItemsDialog
        cargoItems={items}
        open={open}
        handleClose={() => {
          setOpen(false);
        }}
        loadProducts={loadProducts}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            slotProps={{
              textField: { size: "small", sx: { backgroundColor: "white" } },
            }}
            value={date}
            onChange={(newValue) => {
              if (!newValue) return;
              setDate(newValue);
            }}
          />
        </LocalizationProvider>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid
            xs={6}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <TextField
              size="small"
              label={"Carga"}
              sx={{ backgroundColor: "white" }}
              onChange={(e) => {
                setFilteredCargos(
                  cargos.filter((cargo) =>
                    cargo.reference.includes(e.target.value)
                  )
                );
              }}
            />
          </Grid>
          <Grid
            xs={6}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <TextField
              size="small"
              label={"Cliente"}
              sx={{ backgroundColor: "white" }}
              onChange={(e) => {
                setFilteredCargos(
                  cargos.filter((cargo) =>
                    cargo.clientName
                      .toLowerCase()
                      .includes(e.target.value.toLowerCase())
                  )
                );
              }}
            />
          </Grid>
          {/* <Grid
            xs={4}
            sx={{
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Select
              size="small"
              sx={{ width: "100%" }}
              onChange={(e) => {
                const value = e.target.value as unknown as Route;
                setFilteredCargos(
                  cargos.filter((cargo) => cargo.route === value)
                );
              }}
            >
              <MenuItem value={Route.saoMateus}>São Mateus</MenuItem>
              <MenuItem value={Route.teixeira}>Teixeira</MenuItem>
              <MenuItem value={Route.linhares}>Linhares</MenuItem>
              <MenuItem value={Route.interior}>Interior</MenuItem>
            </Select>
          </Grid> */}
        </Grid>
      </Box>
      <Divider>
        <Button startIcon={<SearchRoundedIcon />} variant="contained">
          Pesquisar
        </Button>
      </Divider>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => {
            navigate("new");
          }}
        >
          <AddIcon />
        </Fab>
      </Box>
      {delayedCargos.map((cargo) => {
        return (
          <CargoCard
            delayed
            key={cargo.id!}
            cargo={cargo}
            printCargoLabel={printCargoLabel}
            onEdit={() => {
              setCargoId(cargo.id!);
              setOverwriteOpen(true);
            }}
            onDelete={() => {
              setCargoId(cargo.id!);
              setDeleteOpen(true);
            }}
            onShowProducts={() => {
              setItems(cargo.cargoItems);
              setOpen(true);
            }}
          />
        );
      })}
      {filteredCargos.map((cargo) => {
        return (
          <CargoCard
            key={cargo.id!}
            cargo={cargo}
            printCargoLabel={printCargoLabel}
            onEdit={() => {
              setCargoId(cargo.id!);
              setOverwriteOpen(true);
            }}
            onDelete={() => {
              setCargoId(cargo.id!);
              setDeleteOpen(true);
            }}
            onShowProducts={() => {
              setItems(cargo.cargoItems);
              setOpen(true);
            }}
          />
        );
      })}
    </Stack>
  );
};
